import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { auth } from '../firebase/config';

type User = {
  uid: string;
  name: string;
  login: string;
  email: string;
  avatar_url: string;
  token?: string;
} | null;

type AuthContextType = {
  user: User;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setGitHubToken: (token: string) => Promise<void>;
  clearToken: () => void;
  octokit: any | null;
  githubConnected: boolean;
};

const GITHUB_STORAGE_KEY = 'translatorx_github_auth';
const GITHUB_API_BASE = 'https://api.github.com';

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: false,
  error: null,
  login: async () => {},
  logout: async () => {},
  setGitHubToken: async () => {},
  clearToken: () => {},
  octokit: null,
  githubConnected: false,
});

type StoredGitHubAuth = {
  token: string;
  login: string;
  name: string;
  email: string;
  avatar_url: string;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [octokit, setOctokit] = useState<any>(null);
  const [githubAuth, setGithubAuth] = useState<StoredGitHubAuth | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem(GITHUB_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as StoredGitHubAuth;
        setGithubAuth(parsed);
        setOctokit({ token: parsed.token });
      } catch (err) {
        localStorage.removeItem(GITHUB_STORAGE_KEY);
      }
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      const fallbackLogin = firebaseUser.email?.split('@')[0] || firebaseUser.uid;
      setUser({
        uid: firebaseUser.uid,
        name: githubAuth?.name || firebaseUser.displayName || fallbackLogin,
        login: githubAuth?.login || fallbackLogin,
        email: firebaseUser.email || githubAuth?.email || '',
        avatar_url: githubAuth?.avatar_url || firebaseUser.photoURL || '',
        token: githubAuth?.token,
      });
      setLoading(false);
    });

    return unsubscribe;
  }, [githubAuth]);

  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      console.error('Firebase login error:', err);
      setError(err.message || 'Failed to sign in with email and password');
    } finally {
      setLoading(false);
    }
  };

  const setGitHubToken = async (token: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${GITHUB_API_BASE}/user`, {
        headers: {
          Accept: 'application/vnd.github+json',
          Authorization: `Bearer ${token}`,
          'X-GitHub-Api-Version': '2022-11-28',
        },
      });

      if (!response.ok) {
        throw new Error(`GitHub token validation failed (${response.status})`);
      }

      const userData = (await response.json()) as {
        login: string;
        name?: string | null;
        email?: string | null;
        avatar_url: string;
      };

      const githubProfile: StoredGitHubAuth = {
        token,
        login: userData.login,
        name: userData.name || userData.login,
        email: userData.email || '',
        avatar_url: userData.avatar_url,
      };

      setGithubAuth(githubProfile);
      setOctokit({ token });
      localStorage.setItem(GITHUB_STORAGE_KEY, JSON.stringify(githubProfile));
    } catch (err: any) {
      console.error('GitHub token setup error:', err);
      setError(err.message || 'Failed to validate GitHub token');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearToken = () => {
    localStorage.removeItem(GITHUB_STORAGE_KEY);
    setGithubAuth(null);
    setOctokit(null);
    setUser((current) =>
      current
        ? {
            ...current,
            token: undefined,
            login: current.email?.split('@')[0] || current.uid,
            name: current.name || current.email?.split('@')[0] || current.uid,
            avatar_url: current.avatar_url || '',
          }
        : current
    );
  };

  const logout = async () => {
    setLoading(true);
    setError(null);
    try {
      await firebaseSignOut(auth);
    } catch (err: any) {
      console.error('Logout error:', err);
      setError(err.message || 'Failed to logout');
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    setGitHubToken,
    clearToken,
    octokit,
    githubConnected: Boolean(githubAuth?.token),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
